import { memo, useState } from 'react'
import { textFormatPresets } from '@finanzcheck/catalyst-renderless-components'
import { observer } from 'mobx-react'
import { useFormState } from 'react-final-form'

import ConditionalField from 'components/ConditionalField'
import PathGenderSelect from 'components/PathGenderSelect'
import PathInput from 'components/PathInput'
import PathPhoneInput from 'components/PathPhoneInput'
import PathDateInput from 'components/PathDateInput'
import ProgressiveDisclosed from 'components/ProgressiveDisclosed'
import DebtorsChoice from 'components/DebtorChoice'
import DebtorsTitle from 'components/Smava/DebtorTitle'
import {
  progressiveDisclosureValidator,
  VALIDATOR_LENGTH,
  VALIDATOR_DOB,
  VALIDATOR_PHONE,
  VALIDATOR_EMAIL
} from 'validation'

import { AutoFillType } from 'utils/autofill'
import { Typography } from '@mui/material'

import ConditionalWrapper from 'components/ConditionalWrapper'
import { useStores } from 'stores/utils/hooks/useStores'

import { Debtor } from './PersonalForm.interface'

interface Props {
  debtor: Debtor
  dependson?: string
  showTooltipOnFieldClick?: boolean
  isTooltipHidden?: boolean
  scrollOnFocusEnabled?: boolean
}

interface FormProps {
  [key: string]: string
}

const PersonalForm = observer(
  ({
    debtor = Debtor.PRIMARY,
    dependson,
    showTooltipOnFieldClick = false,
    isTooltipHidden = false,
    scrollOnFocusEnabled = false
  }: Props) => {
    const { values } = useFormState()
    const {
      traversal,
      navigation: { emailFieldDisabled }
    } = useStores()

    const primaryDebtorData = values.debtors.primary

    const [formObject, upateFormObject] = useState<FormProps>({})

    function updateFormObjectFunc<T>(key: string, value: T) {
      const formObjectClone = { ...formObject }
      if (value) {
        formObjectClone[key] = value as unknown as string
        upateFormObject(formObjectClone)
      }
    }

    const disableEmailField =
      !!traversal.data.system.leadId?.length || emailFieldDisabled

    return (
      <ConditionalWrapper dependson={dependson}>
        <ConditionalField path={`debtors.${debtor}`}>
          <Typography variant="h4">Persönliche Angaben</Typography>

          <PathGenderSelect
            path={`debtors.${debtor}.personal.gender`}
            outerLabel
            outlinedChoice
          />
          <ProgressiveDisclosed
            value={primaryDebtorData.personal?.gender}
            validator={progressiveDisclosureValidator(VALIDATOR_LENGTH)}
          >
            <PathInput
              path={`debtors.${debtor}.personal.firstname`}
              autoComplete={AutoFillType.OFF}
              acceptedInput={/\D/}
              trackOnChange
              delayInMs={0}
              formatters={textFormatPresets.name}
              outerLabel
              smallTextField
              showTooltipOnFieldClick={showTooltipOnFieldClick}
              isTooltipHidden={isTooltipHidden}
              scrollOnFocusEnabled={scrollOnFocusEnabled}
            />
          </ProgressiveDisclosed>
          <ProgressiveDisclosed
            value={primaryDebtorData.personal?.firstname}
            validator={progressiveDisclosureValidator(VALIDATOR_LENGTH)}
          >
            <PathInput
              path={`debtors.${debtor}.personal.lastname`}
              autoComplete={AutoFillType.OFF}
              acceptedInput={/\D/}
              trackOnChange
              delayInMs={0}
              outerLabel
              smallTextField
              showTooltipOnFieldClick={showTooltipOnFieldClick}
              isTooltipHidden={isTooltipHidden}
              scrollOnFocusEnabled={scrollOnFocusEnabled}
            />
          </ProgressiveDisclosed>
          <ProgressiveDisclosed
            value={primaryDebtorData.personal?.lastname}
            validator={progressiveDisclosureValidator(VALIDATOR_LENGTH)}
          >
            <PathDateInput
              path={`debtors.${debtor}.personal.birthday`}
              autoComplete={AutoFillType.OFF}
              onBlur={(value) => updateFormObjectFunc('birthday', value)}
              outerLabel
              smallTextField
              showTooltipOnFieldClick={showTooltipOnFieldClick}
              isTooltipHidden={isTooltipHidden}
              scrollOnFocusEnabled={scrollOnFocusEnabled}
            />
          </ProgressiveDisclosed>
          <ProgressiveDisclosed
            value={primaryDebtorData.personal?.birthday}
            validator={progressiveDisclosureValidator(VALIDATOR_DOB)}
          >
            <PathPhoneInput
              path={`debtors.${debtor}.contact.phoneNumber`}
              autoComplete={AutoFillType.DISABLED_HARD}
              tooltip="Die Telefonnummer wird für eventuelle Rückfragen benötigt."
              trackOnChange
              isPhoneTracked
              outerLabel
              smallTextField
              showTooltipOnFieldClick={showTooltipOnFieldClick}
              isTooltipHidden={isTooltipHidden}
            />
          </ProgressiveDisclosed>
          <ProgressiveDisclosed
            value={primaryDebtorData?.contact?.phoneNumber}
            validator={progressiveDisclosureValidator(VALIDATOR_PHONE)}
          >
            <PathInput
              path={`debtors.${debtor}.contact.email`}
              type="email"
              autoComplete={AutoFillType.DISABLED_HARD}
              trackOnChange
              disabled={disableEmailField}
              outerLabel
              smallTextField
              showTooltipOnFieldClick={showTooltipOnFieldClick}
              isTooltipHidden={isTooltipHidden}
              disablePatchField
              scrollOnFocusEnabled={scrollOnFocusEnabled}
            />
          </ProgressiveDisclosed>
          <ProgressiveDisclosed
            value={primaryDebtorData?.contact?.email}
            validator={progressiveDisclosureValidator(VALIDATOR_EMAIL)}
          >
            <>
              <DebtorsTitle />
              <DebtorsChoice outlinedChoice />
            </>
          </ProgressiveDisclosed>
        </ConditionalField>
      </ConditionalWrapper>
    )
  }
)

export default memo(PersonalForm)
